import { User, UserRole, UserPermissions } from '../utils/types';

/**
 * الحصول على الصلاحيات الافتراضية بناءً على دور المستخدم
 * @param role دور المستخدم
 * @returns صلاحيات المستخدم الافتراضية
 */
export const getDefaultPermissions = (role: UserRole): UserPermissions => {
  // صلاحيات مشتركة لجميع الأدوار
  const basePermissions: UserPermissions = {
    // صلاحيات الوصول للصفحات
    canAccessDashboard: false,
    canAccessInventory: false,
    canAccessPOS: false,
    canAccessMaintenance: false,
    canAccessCustomers: false,
    canAccessSettings: false,
    canAccessReports: false,
    canModifyInventory: false,
    canAccessDebts: false,
    canViewExpenses: false,
    canViewBasicExpenses: false,
    canViewDailyExpenses: false,
    canAddExpenses: false,
    canEditExpenses: false,
    canDeleteExpenses: false,

    // صلاحيات المخزون
    canViewInventory: false,
    canAddInventory: false,
    canEditInventory: false,
    canDeleteInventory: false,
    canTransferInventory: false,

    // صلاحيات المبيعات
    canViewSales: false,
    canCreateSales: false,
    canEditSales: false,
    canDeleteSales: false,
    canApplyDiscount: false,

    // صلاحيات الصيانة
    canViewMaintenance: false,
    canCreateMaintenance: false,
    canEditMaintenance: false,
    canDeleteMaintenance: false,
    canAssignTechnician: false,
    canChangeMaintStatus: false,

    // صلاحيات العملاء
    canViewCustomers: false,
    canAddCustomers: false,
    canEditCustomers: false,
    canDeleteCustomers: false,

    // صلاحيات الفروع والمخازن
    canManageBranches: false,
    canManageWarehouses: false,

    // صلاحيات المستخدمين
    canManageUsers: false,
    canViewUsers: false,

    // صلاحيات الإعدادات
    canManageSettings: false,
    canManageBackup: false,
  };

  // تعيين الصلاحيات حسب الدور
  switch (role) {
    case UserRole.ADMIN:
      // مدير النظام لديه جميع الصلاحيات
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessInventory: true,
        canAccessPOS: true,
        canAccessMaintenance: true,
        canAccessCustomers: true,
        canAccessSettings: true,
        canAccessReports: true,
        canModifyInventory: true,
        canAccessDebts: true,
        canViewExpenses: true,
        canViewBasicExpenses: true,
        canViewDailyExpenses: true,
        canAddExpenses: true,
        canEditExpenses: true,
        canDeleteExpenses: true,

        canViewInventory: true,
        canAddInventory: true,
        canEditInventory: true,
        canDeleteInventory: true,
        canTransferInventory: true,

        canViewSales: true,
        canCreateSales: true,
        canEditSales: true,
        canDeleteSales: true,
        canApplyDiscount: true,

        canViewMaintenance: true,
        canCreateMaintenance: true,
        canEditMaintenance: true,
        canDeleteMaintenance: true,
        canAssignTechnician: true,
        canChangeMaintStatus: true,

        canViewCustomers: true,
        canAddCustomers: true,
        canEditCustomers: true,
        canDeleteCustomers: true,

        canManageBranches: true,
        canManageWarehouses: true,

        canManageUsers: true,
        canViewUsers: true,

        canManageSettings: true,
        canManageBackup: true,
      };

    case UserRole.BRANCH_MANAGER:
      // مدير الفرع لديه صلاحيات إدارة الفرع الخاص به
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessInventory: true,
        canAccessPOS: true,
        canAccessMaintenance: true,
        canAccessCustomers: true,
        canAccessReports: true,
        canModifyInventory: true,
        canAccessDebts: true,
        canViewExpenses: true,
        canViewBasicExpenses: true,
        canViewDailyExpenses: true,
        canAddExpenses: true,
        canEditExpenses: true,
        canDeleteExpenses: true,

        canViewInventory: true,
        canAddInventory: true,
        canEditInventory: true,
        canDeleteInventory: true,
        canTransferInventory: true,

        canViewSales: true,
        canCreateSales: true,
        canEditSales: true,
        canDeleteSales: true,
        canApplyDiscount: true,

        canViewMaintenance: true,
        canCreateMaintenance: true,
        canEditMaintenance: true,
        canDeleteMaintenance: true,
        canAssignTechnician: true,
        canChangeMaintStatus: true,

        canViewCustomers: true,
        canAddCustomers: true,
        canEditCustomers: true,
        canDeleteCustomers: true,

        canManageWarehouses: true,

        canViewUsers: true,
        canManageBackup: true,
      };

    case UserRole.WAREHOUSE_MANAGER:
      // مدير المخزن لديه صلاحيات إدارة المخزون
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessInventory: true,
        canAccessReports: true,
        canModifyInventory: true,
        canViewExpenses: true,
        canViewBasicExpenses: true,
        canViewDailyExpenses: true,
        canAddExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,

        canViewInventory: true,
        canAddInventory: true,
        canEditInventory: true,
        canDeleteInventory: true,
        canTransferInventory: true,

        canViewSales: true,

        canViewMaintenance: true,

        canViewCustomers: true,

        canManageWarehouses: true,
        canManageBackup: true,
      };

    case UserRole.TECHNICIAN:
      // فني الصيانة لديه صلاحيات إدارة الصيانة
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessMaintenance: true,
        canAccessInventory: true,
        canViewExpenses: true,
        canViewBasicExpenses: false,
        canViewDailyExpenses: true,
        canAddExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,

        canViewInventory: true,

        canViewMaintenance: true,
        canCreateMaintenance: true,
        canEditMaintenance: true,
        canChangeMaintStatus: true,

        canViewCustomers: true,
      };

    case UserRole.CASHIER:
      // أمين الصندوق لديه صلاحيات المبيعات
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessPOS: true,
        canAccessDebts: true,
        canViewExpenses: true,
        canViewBasicExpenses: false,
        canViewDailyExpenses: true,
        canAddExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,

        canViewSales: true,
        canCreateSales: true,
        canEditSales: true,
        canDeleteSales: true,
        canApplyDiscount: true,

        canViewCustomers: true,
        canAddCustomers: true,
        canEditCustomers: true,
      };

    case UserRole.INVENTORY_CLERK:
      // موظف المخزن لديه صلاحيات محدودة للمخزون
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessInventory: true,
        canModifyInventory: true,
        canViewExpenses: true,
        canViewBasicExpenses: false,
        canViewDailyExpenses: true,
        canAddExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,

        canViewInventory: true,
        canAddInventory: true,
        canEditInventory: true,
        canTransferInventory: true,
      };

    case UserRole.SALES:
      // مندوب المبيعات لديه صلاحيات المبيعات والعملاء
      return {
        ...basePermissions,
        canAccessDashboard: true,
        canAccessPOS: true,
        canAccessDebts: true,
        canAccessCustomers: true,
        canViewExpenses: true,
        canViewBasicExpenses: false,
        canViewDailyExpenses: true,
        canAddExpenses: false,
        canEditExpenses: false,
        canDeleteExpenses: false,

        canViewInventory: true,

        canViewSales: true,
        canCreateSales: true,

        canViewCustomers: true,
        canAddCustomers: true,
        canEditCustomers: true,
      };

    case UserRole.CUSTOMER:
      // العميل لديه صلاحيات محدودة جدًا (للمتجر فقط)
      return {
        ...basePermissions,
        // لا توجد صلاحيات إدارية للعملاء
        // تعيين جميع صلاحيات الوصول إلى لوحة التحكم إلى false بشكل صريح
        canAccessDashboard: false,
        canAccessInventory: false,
        canAccessPOS: false,
        canAccessMaintenance: false,
        canAccessCustomers: false,
        canAccessSettings: false,
        canAccessReports: false,
        canModifyInventory: false,
        canAccessDebts: false,
        canViewExpenses: false,

        // صلاحيات خاصة بالمتجر يمكن إضافتها هنا
        // مثال: صلاحية عرض طلبات المستخدم نفسه
      };

    default:
      return basePermissions;
  }
};

/**
 * التحقق من صلاحية المستخدم
 * @param user المستخدم
 * @param permission الصلاحية المطلوبة
 * @returns هل لديه الصلاحية
 */
export const hasPermission = (user: User | null, permission: keyof UserPermissions): boolean => {
  if (!user) return false;

  // إذا لم تكن هناك صلاحيات محددة، استخدم الصلاحيات الافتراضية
  if (!user.permissions) {
    const defaultPermissions = getDefaultPermissions(user.role);
    return defaultPermissions[permission] || false;
  }

  return user.permissions[permission] || false;
};

/**
 * التحقق من صلاحية المستخدم للوصول إلى مخزن محدد
 * @param user المستخدم
 * @param warehouseId معرف المخزن
 * @returns هل لديه صلاحية الوصول
 */
export const canAccessWarehouse = (user: User | null, warehouseId: string): boolean => {
  if (!user) {
    console.log('❌ No user provided to canAccessWarehouse');
    return false;
  }

  console.log(`🔐 Checking warehouse access for user ${user.name} (${user.role}) to warehouse ${warehouseId}`);
  console.log('📋 User allowedWarehouses:', user.allowedWarehouses);

  // مدير النظام لديه صلاحية الوصول إلى جميع المخازن
  if (user.role === UserRole.ADMIN) {
    console.log('✅ Admin user - access granted');
    return true;
  }

  // إذا لم تكن هناك قائمة مخازن محددة، السماح بالوصول (للتوافق مع النظام القديم)
  // هذا يضمن أن المستخدمين القدامى يمكنهم الوصول للمخازن
  if (!user.allowedWarehouses || user.allowedWarehouses.length === 0) {
    console.log('✅ No allowedWarehouses specified - granting access for backward compatibility');
    return true;
  }

  // التحقق من المخازن المسموح بها لأي مستخدم
  if (user.allowedWarehouses && user.allowedWarehouses.includes(warehouseId)) {
    console.log('✅ Warehouse found in user allowedWarehouses');
    return true;
  }

  console.log('❌ Warehouse access denied - not in allowedWarehouses list');
  return false;
};

/**
 * التحقق من صلاحية المستخدم للوصول إلى فرع محدد
 * @param user المستخدم
 * @param branchId معرف الفرع
 * @returns هل لديه صلاحية الوصول
 */
export const canAccessBranch = (user: User | null, branchId: string): boolean => {
  if (!user) return false;

  // مدير النظام لديه صلاحية الوصول إلى جميع الفروع
  if (user.role === UserRole.ADMIN) {
    return true;
  }

  // التحقق من الفرع المرتبط بالمستخدم
  if (user.branchId === branchId) {
    return true;
  }

  return false;
};

/**
 * التحقق من صلاحية المستخدم للوصول إلى لوحة التحكم
 * @param user المستخدم
 * @returns هل لديه صلاحية الوصول إلى لوحة التحكم
 */
export const canAccessAdminPanel = (user: User | null): boolean => {
  if (!user) return false;

  // العملاء لا يمكنهم الوصول إلى لوحة التحكم
  if (user.role === UserRole.CUSTOMER) {
    return false;
  }

  // التحقق من صلاحية الوصول إلى لوحة التحكم
  return hasPermission(user, 'canAccessDashboard');
};

export default {
  getDefaultPermissions,
  hasPermission,
  canAccessWarehouse,
  canAccessBranch,
  canAccessAdminPanel
};
